import type { CSSProperties } from 'react';

import type { AxisScale, SharedAxisProps } from './types.js';
import { getTickValueAsNumber } from '../../../../core/utils/get-tick-value-as-number.js';
import { getTicks } from '../../../../core/utils/get-ticks.js';
import type { Point } from '../../types/point.js';
import type { TextProps } from '../../types/text-props.js';

/**
 * Hardcoded font size fallback matching visx behavior.
 *
 * visx computes `maxTickLabelFontSize` and per-tick `tickLabelFontSize`
 * using `Math.max(10, typeof fontSize === 'number' ? fontSize : 0)`.
 * Since Strato always passes fontSize as a CSS variable string
 * (e.g. "var(--dt-typography-text-small-emphasized-size, 12px)"),
 * the typeof check always fails and the result is always 10.
 */
const TICK_LABEL_FONT_SIZE = 10;

/** Matches visx Text component's wrapping SVG overflow style. */
const SVG_OVERFLOW_STYLE: CSSProperties = { overflow: 'visible' };

const defaultTextProps: Partial<TextProps> = {
  textAnchor: 'middle',
  fontFamily: 'Arial',
  fontSize: 10,
  fill: '#222',
};

type FormattedValue = string | undefined;

/**
 * Computes the `dy` offset for the first `<tspan>` in visx Text's SVG structure.
 * visx Text uses `verticalAnchor` + `capHeight` (0.71em) to shift single-line text:
 *   'start'  → "0.71em"  (text hangs below y)
 *   'middle' → "0.355em" (text centered on y)
 *   'end'    → "0"       (text baseline at y)
 */
function getStartDy(verticalAnchor: string): string {
  if (verticalAnchor === 'start') {
    return '0.71em';
  }
  if (verticalAnchor === 'middle') {
    return '0.355em';
  }
  return '0';
}

/**
 * Returns a tick position function that handles band scale centering.
 * For band/point scales, adds `bandwidth()/2` to center ticks within bands.
 * For continuous scales, returns the scale itself as the position function.
 */
function getTickPosition(
  scale: AxisScale,
): (value: Parameters<typeof scale>[0]) => number | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = scale as any;

  if ('bandwidth' in s) {
    let offset: number = s.bandwidth();
    offset /= 2;
    if (s.round()) {
      offset = Math.round(offset);
    }
    return (d) => {
      const scaledValue = s(d);
      return typeof scaledValue === 'number'
        ? scaledValue + offset
        : scaledValue;
    };
  }

  return scale as (value: Parameters<typeof scale>[0]) => number | undefined;
}

/**
 * Returns a tick format function from the scale, falling back to string conversion.
 */
function getTickFormatter(scale: AxisScale) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = scale as any;
  if ('tickFormat' in s) {
    return s.tickFormat();
  }
  return String;
}

/**
 * Returns a point as-is for horizontal axes.
 */
function createHorizontalPoint(coords: Point): Point {
  return coords;
}

/**
 * Returns a point with x/y swapped for vertical axes.
 */
function createVerticalPoint(coords: Point): Point {
  return { x: coords.y, y: coords.x };
}

/**
 * Computes the axis label position and rotation for each orientation.
 *
 * With Strato's hardcoded values (tickLength=0, fontSize is always a CSS var string):
 * - tickLabelFontSize is always 10
 * - yBottomOffset is always 0 (typeof CSS-var-string !== 'number')
 *
 * Bottom: x = rangeMidpoint, y = labelOffset + 10
 * Top:    x = rangeMidpoint, y = -(labelOffset + 10)
 * Left:   x = -rangeMidpoint, y = -labelOffset, transform = "rotate(-90)"
 * Right:  x = +rangeMidpoint, y = -labelOffset, transform = "rotate(90)"
 */
function getLabelTransform({
  labelOffset,
  labelProps,
  orientation,
  range,
}: {
  labelOffset: number;
  labelProps: Partial<TextProps>;
  orientation: string;
  range: number[];
}): { x: number; y: number; transform?: string } {
  const sign = orientation === 'left' || orientation === 'top' ? -1 : 1;

  if (orientation === 'top' || orientation === 'bottom') {
    const yBottomOffset =
      orientation === 'bottom' && typeof labelProps.fontSize === 'number'
        ? labelProps.fontSize
        : 0;

    return {
      x: (Number(range[0]) + Number(range[range.length - 1])) / 2,
      y: sign * (labelOffset + TICK_LABEL_FONT_SIZE + yBottomOffset),
      transform: undefined,
    };
  }

  return {
    x: sign * ((Number(range[0]) + Number(range[range.length - 1])) / 2),
    y: -labelOffset,
    transform: `rotate(${sign * 90})`,
  };
}

/**
 * AxisPrimitive accepts SharedAxisProps for backward compatibility, plus extra
 * props that Axis.tsx passes through from AxisProps (role, aria-label).
 * Dead-code props (tickLength, hideTicks, hideAxisLine, etc.) are accepted
 * but ignored. This allows Axis.tsx to spread restProps without filtering.
 * The dead props will be cleaned up in APPDEV-17081.
 */
type AxisPrimitiveProps = SharedAxisProps<AxisScale> & {
  /** Passed through from Axis.tsx; applied to outer <svg>, not consumed here. */
  role?: string;
  /** Passed through from Axis.tsx; applied to outer <svg>, not consumed here. */
  'aria-label'?: string;
};

/**
 * Local replacement for `@visx/axis` Axis rendering component.
 *
 * Flattens visx's 3-layer architecture (Axis.js -> AxisRenderer.js -> Ticks.js)
 * into a single component. This simplification is possible because Strato always
 * hardcodes tickLength=0, hideTicks=true, hideAxisLine=true -- meaning tick lines,
 * axis lines, and related props are dead code.
 *
 * The component:
 * 1. Computes tick positions (using getTicks + getTickPosition + coerceNumber + createHorizontalPoint/createVerticalPoint)
 * 2. Calls tickComponent for each tick with the same contract as visx
 * 3. Renders the axis label using getLabelTransform math + plain SVG text element
 *
 * @internal
 */
export function AxisPrimitive({
  scale,
  orientation = 'bottom',
  tickComponent,
  tickLabelProps,
  tickFormat,
  numTicks = 10,
  tickValues,
  hideZero = false,
  label = '',
  labelClassName,
  labelOffset = 14,
  labelProps,
  left = 0,
  top: topOffset = 0,
  axisClassName,
  innerRef,
}: AxisPrimitiveProps) {
  const isTop = orientation === 'top';
  const horizontal = isTop || orientation === 'bottom';

  const tickPosition = getTickPosition(scale);
  const format = tickFormat ?? getTickFormatter(scale);
  const filteredTickValues = (tickValues ?? getTicks(scale, numTicks))
    .filter(
      (value: string | number) => !hideZero || (value !== 0 && value !== '0'),
    )
    .map((value: string | number, index: number) => ({ value, index }));

  const pointCreator = horizontal ? createHorizontalPoint : createVerticalPoint;

  const ticks = filteredTickValues.map(
    ({ value, index }: { value: string | number; index: number }) => {
      const scaledValue = getTickValueAsNumber(tickPosition(value)) as number;
      return {
        value,
        index,
        to: pointCreator({ x: scaledValue, y: 0 }),
        formattedValue: format(
          value,
          index,
          filteredTickValues,
        ) as FormattedValue,
      };
    },
  );

  // Resolve tick label props per tick (matching visx AxisRenderer behavior)
  const tickLabelPropsDefault = {
    ...defaultTextProps,
    ...(typeof tickLabelProps === 'object' ? tickLabelProps : null),
  };

  const allTickLabelProps: Partial<TextProps>[] = ticks.map(
    ({ value, index }: { value: string | number; index: number }) =>
      typeof tickLabelProps === 'function'
        ? tickLabelProps(value, index, filteredTickValues)
        : tickLabelPropsDefault,
  );

  const combinedLabelProps = { ...defaultTextProps, ...labelProps };
  const labelTransform = label
    ? getLabelTransform({
        labelOffset,
        labelProps: combinedLabelProps,
        orientation,
        range: scale.range() as number[],
      })
    : undefined;

  return (
    <g
      className={axisClassName ? `visx-axis ${axisClassName}` : 'visx-axis'}
      ref={innerRef}
      transform={`translate(${left}, ${topOffset})`}
    >
      {ticks.map(({ value, index, to, formattedValue }) => {
        const currentTickLabelProps = allTickLabelProps[index] ?? {};

        // visx computes tickYCoord by adding fontSize offset for horizontal bottom axes.
        // Since fontSize is always a CSS var string, tickLabelFontSize always falls back to 10.
        const tickYCoord =
          to.y +
          (horizontal && orientation !== 'top' ? TICK_LABEL_FONT_SIZE : 0);

        // visx Text path: tickLabelProps.x/y take precedence over computed values when provided.
        const effectiveX = currentTickLabelProps.x ?? to.x;
        const effectiveY = currentTickLabelProps.y ?? tickYCoord;
        const dxOffset = currentTickLabelProps.dx ?? 0;
        const dyOffset = currentTickLabelProps.dy ?? 0;
        const startDy = getStartDy(
          currentTickLabelProps.verticalAnchor ?? 'end',
        );
        const tickAngle = currentTickLabelProps.angle;
        const tickTransform = tickAngle
          ? `rotate(${tickAngle}, ${effectiveX}, ${effectiveY})`
          : undefined;

        const defaultTickElement = (
          <svg
            x={dxOffset}
            y={dyOffset}
            fontSize={currentTickLabelProps.fontSize}
            style={SVG_OVERFLOW_STYLE}
          >
            <text
              x={effectiveX}
              y={effectiveY}
              transform={tickTransform}
              textAnchor={currentTickLabelProps.textAnchor}
              fontFamily={currentTickLabelProps.fontFamily}
              fontSize={currentTickLabelProps.fontSize}
              fill={currentTickLabelProps.fill}
            >
              <tspan x={effectiveX} dy={startDy}>
                {formattedValue}
              </tspan>
            </text>
          </svg>
        );

        return (
          <g key={`visx-tick-${value}-${index}`} className="visx-axis-tick">
            {tickComponent
              ? tickComponent({
                  ...currentTickLabelProps,
                  x: to.x,
                  y: tickYCoord,
                  formattedValue,
                })
              : defaultTickElement}
          </g>
        );
      })}

      {/* x/y attributes rather than transform="translate" so that <tspan x> stays in the
          same coordinate system and doesn't double-offset the label position. */}
      {label && labelTransform && (
        <svg
          x={0}
          y={0}
          fontSize={combinedLabelProps.fontSize}
          style={SVG_OVERFLOW_STYLE}
        >
          <text
            className={
              labelClassName
                ? `visx-axis-label ${labelClassName}`
                : 'visx-axis-label'
            }
            x={labelTransform.x}
            y={labelTransform.y}
            transform={labelTransform.transform}
            textAnchor={combinedLabelProps.textAnchor}
            fontFamily={combinedLabelProps.fontFamily}
            fontSize={combinedLabelProps.fontSize}
            fill={combinedLabelProps.fill}
          >
            <tspan x={labelTransform.x} dy="0">
              {label}
            </tspan>
          </text>
        </svg>
      )}
    </g>
  );
}
